import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  CircleSlash,
  Mail,
  Phone,
  RefreshCcw,
  User as UserIcon,
} from "lucide-react";
import { userDisplayLabel } from "@lumika/shared";
import type { Activity, Contact, User } from "@lumika/shared";
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  cn,
  toast,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import { ContactAvatar } from "../../components/ContactAvatar";
import { formatDateTime, humanize, summarizeChangeBody } from "../../lib/format";

// Opportunity vocabulary — mirrors the LeadSchema `type`/`status` enums.
const LEAD_TYPES = ["sales", "service", "parts"] as const;
const LEAD_STATUSES = ["new", "working", "won", "lost"] as const;
type LeadType = (typeof LEAD_TYPES)[number];
type LeadStatus = (typeof LEAD_STATUSES)[number];
// Plain-English pipeline labels (kept local to avoid a cross-file shared module).
const LEAD_STATUS_LABEL: Record<LeadStatus, string> = {
  new: "New",
  working: "Working",
  won: "Won",
  lost: "Lost",
};
const NO_TYPE = "__none__";

export function DealershipLeadDetailPage() {
  const { dealershipId, leadId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const conversationQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "lead", leadId],
    queryFn: () => adminApi.getLead(dealershipId!, leadId!),
    enabled: Boolean(dealershipId && leadId),
  });
  const detailQuery = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId!),
    enabled: Boolean(dealershipId),
  });
  // Fetch just this lead's contact by id — scanning the paginated
  // contact list would miss anyone past the first page.
  const contactId = conversationQuery.data?.contactId;
  const contactQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "contact", contactId],
    enabled: Boolean(dealershipId && contactId),
    queryFn: () => adminApi.getContact(dealershipId!, contactId!),
  });
  const activitiesQuery = useQuery({
    queryKey: ["admin-bdc", dealershipId, "activities", "lead", leadId],
    queryFn: () =>
      adminApi.listBdcActivities(dealershipId!, {
        leadId,
        pageSize: 100,
      }),
    enabled: Boolean(dealershipId && leadId),
  });

  const lead = conversationQuery.data;
  const users = useMemo(() => detailQuery.data?.users ?? [], [detailQuery.data]);
  const rooftops = useMemo(() => detailQuery.data?.rooftops ?? [], [detailQuery.data]);
  const contact = contactQuery.data ?? null;

  const updateMutation = useMutation({
    mutationFn: (input: Parameters<typeof adminApi.updateLead>[2]) =>
      adminApi.updateLead(dealershipId!, leadId!, input),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ["admin-bdc", dealershipId, "lead", leadId],
      });
      await qc.invalidateQueries({
        queryKey: ["admin-bdc", dealershipId, "leads"],
      });
      toast.success("Lead updated");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (conversationQuery.isLoading) {
    return <PageLoading message="Loading lead…" />;
  }

  if (!lead) {
    return (
      <PageError
        title="Lead not found"
        description={conversationQuery.error?.message}
        action={
          <Button variant="outline" onClick={() => navigate(`/dealerships/${dealershipId}/inbox`)}>
            Back to inbox
          </Button>
        }
      />
    );
  }

  const contactName = contact ? resolveContactName(contact) : "—";
  const assignedUser = lead.assignedUserId
    ? users.find((u) => u.id === lead.assignedUserId)
    : undefined;

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        eyebrow={
          <Link
            to={`/dealerships/${dealershipId}/inbox`}
            className="-ml-2 inline-flex min-h-touch items-center gap-1 rounded-md px-2 text-sm hover:bg-muted hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Inbox
          </Link>
        }
        title={contactName}
        description={
          <span className="inline-flex items-center gap-2">
            <Badge variant="secondary">{humanize(lead.primaryChannel)}</Badge>
            <Badge variant={lead.closed ? "secondary" : "success"}>
              {lead.closed ? "closed" : "open"}
            </Badge>
            {lead.snoozedUntil && (
              <Badge variant="warning">
                Snoozed until {formatDateTime(lead.snoozedUntil)}
              </Badge>
            )}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {!lead.closed ? (
              <Button
                variant="outline"
                onClick={() =>
                  updateMutation.mutate({
                    closed: true,
                    closedAt: new Date().toISOString(),
                  })
                }
                disabled={updateMutation.isPending}
              >
                <CircleSlash className="h-4 w-4" />
                Close
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() =>
                  updateMutation.mutate({ closed: false, closedAt: null })
                }
                disabled={updateMutation.isPending}
              >
                <RefreshCcw className="h-4 w-4" />
                Reopen
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)]">
        <Section title="Activity timeline">
          {activitiesQuery.isLoading ? (
            <PageLoading message="Loading activity…" />
          ) : activitiesQuery.isError ? (
            <PageError
              title="Couldn't load activity"
              description="Try refreshing the page."
            />
          ) : (
            <ActivityTimeline
              activities={activitiesQuery.data?.items ?? []}
              users={users}
              contactName={contactName}
            />
          )}
        </Section>
        <Card>
          <CardContent className="grid gap-5 p-6">
            <SideHeading title="Customer 360" />
            <div className="grid gap-1">
              <div className="text-sm font-semibold">{contactName}</div>
              <div className="text-xs text-muted-foreground">
                {contact?.status === "anonymous" ? "Anonymous touch" : (contact?.status ?? "Lead")}
              </div>
            </div>
            <div className="grid gap-2 text-sm">
              <SideRow icon={<Mail className="h-4 w-4" />} value={contact?.primaryEmail ?? "—"} />
              <SideRow icon={<Phone className="h-4 w-4" />} value={contact?.primaryPhone ?? "—"} />
              <SideRow
                icon={
                  assignedUser ? (
                    <ContactAvatar
                      size="sm"
                      name={userDisplayLabel(assignedUser)}
                      avatarUrl={assignedUser.avatarUrl}
                    />
                  ) : (
                    <UserIcon className="h-4 w-4" />
                  )
                }
                value={
                  lead.assignedUserId
                    ? assignedUser
                      ? userDisplayLabel(assignedUser)
                      : "Assigned operator"
                    : "Unassigned"
                }
              />
              <SideRow
                icon={<CalendarClock className="h-4 w-4" />}
                value={
                  lead.lastActivityAt
                    ? `Last activity ${formatDateTime(lead.lastActivityAt)}`
                    : "No activity yet"
                }
              />
              {lead.slaDueAt && (
                <SideRow
                  icon={<CheckCircle2 className="h-4 w-4" />}
                  value={`SLA due ${formatDateTime(lead.slaDueAt)}`}
                />
              )}
            </div>

            <Separator />

            <div className="grid gap-3">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Opportunity
              </div>
              <div className="grid gap-2">
                <label className="text-xs text-muted-foreground">Type</label>
                <Select
                  value={lead.type ?? NO_TYPE}
                  onValueChange={(value) =>
                    updateMutation.mutate({
                      type: value === NO_TYPE ? null : (value as LeadType),
                    })
                  }
                  disabled={updateMutation.isPending}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_TYPE}>—</SelectItem>
                    {LEAD_TYPES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {humanize(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <label className="text-xs text-muted-foreground">Status</label>
                <Select
                  value={lead.status}
                  onValueChange={(value) =>
                    updateMutation.mutate({ status: value as LeadStatus })
                  }
                  disabled={updateMutation.isPending}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {LEAD_STATUS_LABEL[option]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1">
                <span className="text-xs text-muted-foreground">Interest</span>
                <span className="text-sm">{lead.interest ?? "—"}</span>
              </div>
              <div className="grid gap-1">
                <span className="text-xs text-muted-foreground">Rooftop</span>
                <span className="text-sm">
                  {lead.rooftopId
                    ? (rooftops.find((r) => r.id === lead.rooftopId)?.name ?? "—")
                    : "All rooftops"}
                </span>
              </div>
            </div>

            <Separator />

            <AssignmentControl
              users={users}
              currentUserId={lead.assignedUserId}
              onChange={(value) =>
                updateMutation.mutate({
                  assignedUserId: value === "unassigned" ? null : value,
                })
              }
              disabled={updateMutation.isPending}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ActivityTimeline({
  activities,
  users,
  contactName,
}: {
  activities: Activity[];
  users: User[];
  contactName: string;
}) {
  // Change diffs store raw assignee ids — resolve them to names so an
  // assignment change reads "Ann → Ben", never a UUID.
  const userName = (id: string) => {
    const u = users.find((x) => x.id === id);
    return u ? userDisplayLabel(u) : null;
  };
  if (activities.length === 0) {
    return (
      <EmptyState
        icon={<CalendarClock className="h-5 w-5" />}
        title="No activity"
        description="Messages, notes, and system events on this thread will land here."
      />
    );
  }
  // Render oldest → newest (top → bottom), like the App platform and every
  // messenger. The admin activities endpoint returns newest-first (DESC,
  // shared with the audit log), so reverse a copy for the thread view.
  const ordered = [...activities].reverse();
  return (
    <ol className="flex flex-col gap-3">
      {ordered.map((activity) => {
        return (
          <li
            key={activity.id}
            className={cn(
              "rounded-md border bg-card p-4",
              activity.direction === "inbound" && "border-l-4 border-l-foreground",
              activity.direction === "outbound" && "border-r-4 border-r-foreground",
              activity.direction === "internal" && "bg-muted/40",
            )}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                  {humanize(activity.type)}
                </Badge>
                <span className="text-muted-foreground">
                  {humanize(activity.direction)} ·{" "}
                  {actorLabel(activity, users, contactName)}
                </span>
              </div>
              <time className="text-xs text-muted-foreground">
                {formatDateTime(activity.occurredAt)}
              </time>
            </div>
            {(activity.body || activity.summary) && (
              <p className="mt-2 whitespace-pre-wrap text-sm">
                {summarizeChangeBody(activity.body, { userName }) ??
                  activity.body ??
                  activity.summary}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Who did this — the actual contact's name for contact rows, "AI
 * assistant" for AI actors, and the canonical user label otherwise.
 * AI text replies are written with actorType="user" + the AI user's
 * id, so `kind` — not actorType — is the reliable AI signal. Never a
 * raw UUID, never the literal "Contact".
 */
function actorLabel(activity: Activity, users: User[], contactName: string): string {
  if (activity.actorType === "contact") return contactName;
  const actor = activity.actorUserId
    ? users.find((u) => u.id === activity.actorUserId)
    : undefined;
  if (activity.actorType === "ai" || actor?.kind === "ai") return "AI assistant";
  if (actor) return userDisplayLabel(actor);
  return humanize(activity.actorType);
}

function AssignmentControl({
  users,
  currentUserId,
  onChange,
  disabled,
}: {
  users: User[];
  currentUserId: string | null;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="grid gap-2">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Assignment
      </div>
      <Select
        value={currentUserId ?? "unassigned"}
        onValueChange={onChange}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="unassigned">Unassigned</SelectItem>
          {/* Deactivated users are not assignable (backend rejects them;
              pending is fine) — hide them, EXCEPT the current assignee,
              who stays as a disabled item so the Select keeps rendering
              their name as the value label. */}
          {users
            .filter(
              (u) => u.kind === "human" && (u.status !== "inactive" || u.id === currentUserId),
            )
            .map((user) => (
              <SelectItem key={user.id} value={user.id} disabled={user.status === "inactive"}>
                {userDisplayLabel(user)}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function resolveContactName(contact: Contact): string {
  if (contact.businessName) return contact.businessName;
  const composed = [contact.firstName, contact.lastName].filter(Boolean).join(" ");
  return composed || contact.primaryEmail || contact.primaryPhone || contact.fallbackLabel || "Anonymous";
}

function SideHeading({ title }: { title: string }) {
  return (
    <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
      {title}
    </h3>
  );
}

function SideRow({
  icon,
  value,
}: {
  icon: React.ReactNode;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">{icon}</span>
      <span className="truncate">{value}</span>
    </div>
  );
}
